import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { BaseScene } from '../../scenes/BaseScene.js';
import { TrainSimBoard } from './TrainSimBoard.js';
import { TrainSimTrain } from './TrainSimTrain.js';
import { TrainSimHud } from './TrainSimHud.js';
import { TRAINSIM_CONFIG, TRAINSIM_GRID } from './trainsimConfig.js';
import { GameOverOverlay } from '../../ui/GameOverOverlay.js';
import { LevelCompleteOverlay } from '../../ui/LevelCompleteOverlay.js';
import { ScoreSubmitOverlay } from '../../ui/ScoreSubmitOverlay.js';
import { createThemeBackground } from '../../ui/createThemeBackground.js';
import { SceneChrome } from '../../ui/SceneChrome.js';
import { partyMode, pop as confettiPop } from '../../ui/Confetti.js';
import { t, toggleLang } from '../../config/i18n.js';
import { loadStage } from '../../services/StageLoader.js';
import { FilterPipeline } from '../../systems/GameFilters.js';

const MSG_STYLE = new TextStyle({
    fontFamily: 'Arial', fontSize: 36, fontWeight: 'bold', fill: '#fbbf24', align: 'center',
});

// Danger-rewind highlight window. The moment a crash is detected
// the sim freezes for this many frames and pulses the crashed
// train(s) so the player can see the dangerous situation AND
// read the banner ("Ez egy veszélyes közlekedési helyzet, kérem
// kezdje újra") BEFORE the auto-rewind animation kicks in. Tuned
// for ~2s of dwell, plus the ~0.55s rewind animation = ~2.55s of
// total banner visibility — comfortable for reading the full
// Hungarian sentence without feeling sluggish.
const TRAINSIM_DANGER_HIGHLIGHT_FRAMES = 120;
const TRAINSIM_HIGHLIGHT_ALPHA_MIN = 0.30;
const TRAINSIM_HIGHLIGHT_ALPHA_MAX = 1.00;
// Sin frequency of the highlight pulse, in radians per millisecond.
// Tuned for ~3 visible blinks across the 2s highlight window:
// 3 × 2π ÷ 2000 ≈ 0.0094 rad/ms.
const TRAINSIM_HIGHLIGHT_PULSE_SPEED = 0.0094;

// Post-rewind grace window — how long the proactive
// path-overlap detector stays suppressed after the auto-rewind
// lands. 4s is enough time for the player to flip one or two
// switches to break the conflict before the detector re-arms;
// without this, a rewind landing on a state with still-
// overlapping paths would loop forever.
const TRAINSIM_DANGER_COOLDOWN_FRAMES = 240;

const DEFAULT_STAGE_FILE = 'stage01.json';

export class TrainSimScene extends BaseScene {
    _board = null;
    /** @type {TrainSimTrain[]} */
    _trains = [];

    _worldContainer = null;
    _hudContainer = null;
    _themeBg = null;

    _stage = null;
    _stageLoading = false;

    _gameOver = false;
    _allReachedGoal = false;

    // ── Danger auto-rewind phase ──────────────────────────────────
    // Per client feedback ("Ne legyen ütközés…"), collisions are no
    // longer fatal. The instant a crash WOULD have happened we:
    //   1. Halt the simulator (`_dangerRewindActive = true`).
    //   2. Pulse the crashed train(s) for
    //      `TRAINSIM_DANGER_HIGHLIGHT_FRAMES` frames so the player
    //      can SEE the dangerous situation that's about to be
    //      undone.
    //   3. Show a centred banner: "Ez egy veszélyes közlekedési
    //      helyzet, kérem kezdje újra".
    //   4. Auto-trigger the rewind animation back ~10 seconds — does
    //      NOT consume one of the player's manual rewinds, and falls
    //      back to the oldest available snapshot if there isn't a
    //      full 10s of history yet.
    //   5. When the rewind animation lands, hide the banner and
    //      resume normal play.
    _dangerRewindActive = false;
    _dangerHighlightFrames = 0;
    /** Rewind distance (game-seconds) for the in-flight danger rewind,
     *  set by `_triggerDangerRewind` from the crash reason: the usual
     *  `_REWIND_SECONDS` for collisions/derailments, the longer
     *  `_WRONG_DEST_REWIND_SECONDS` for a wrong-destination arrival. */
    _dangerRewindSeconds = 0;
    /** @type {{ train: import('./TrainSimTrain.js').TrainSimTrain, baseAlpha: number }[]} */
    _dangerHighlightTrains = [];
    /**
     * Frames remaining in the post-rewind grace window during
     * which `_detectImpendingPathCollision` is suppressed. Without
     * this, a rewind that lands on a state where the predicted
     * paths still overlap would re-trigger the detector on the
     * VERY NEXT frame, creating a forever-rewind loop the player
     * couldn't escape. The grace gives the player time to flip a
     * switch or two and break the conflict before the proactive
     * detector re-arms.
     */
    _dangerCooldownFrames = 0;
    /**
     * Two independent inputs feed the visible "paused" state:
     *
     *  • `_userPaused`   — flipped by the HUD PAUSE tile. Sticky:
     *    survives chrome modal open/close cycles.
     *  • `_chromePaused` — flipped by SceneChrome when the player
     *    opens Help / Leaderboard / etc. Auto-resumes when the
     *    chrome modal closes.
     *
     * The single `_paused` flag (consumed by `update()` as the
     * existing short-circuit) is recomputed every time either input
     * changes via `_recomputePause()`. Two flags exist because a
     * chrome resume must NOT cancel the player's deliberate pause —
     * before this split, opening Help and then closing it would
     * silently un-pause a game the player had explicitly frozen.
     */
    _paused = false;
    _userPaused = false;
    _chromePaused = false;
    /**
     * Whether the FOCUS-mode dim overlay is engaged. Mirrored onto
     * the board via `_board.setFocusMode(...)` whenever the player
     * toggles the HUD FOCUS tile. Persisted via `window.__trainsimFocus`
     * so a stage restart (game-over → AGAIN, level-complete → next)
     * keeps the player's chosen visibility mode.
     */
    _focusMode = false;
    /**
     * Per-feature visibility gates, read once from
     * `trainsim.settings` at init. Each gates the corresponding HUD
     * tile so a parent / kiosk operator can turn off the easier-mode
     * assists without touching the code path. `true` means the
     * feature is available; `false` hides the tile completely.
     */
    _pauseEnabled = true;
    _focusEnabled = true;
    _rewindEnabled = true;

    /** Global train-speed multiplier toggled by the HUD speed button.
     *  Persisted across restarts via window.__trainsimSpeed so the
     *  setting survives a scene rebuild (game over → "AGAIN"). */
    _speedMultiplier = 1;
    static _SPEED_STEPS = [1, 2, 4];

    /** Live HUD counters surfaced to the top-left "PONT" / "ÉLET"
     *  cards and the top-right "IDŐ" clock. Updated as trains
     *  reach their goal or crash, and ticked each frame for the
     *  in-world clock. */
    _score = 0;
    /** Lives left — start at 3, a crash costs 1, hitting 0 ends
     *  the game. Lets the player recover from a single mistake
     *  instead of restarting on the first crash like before. */
    _lives = 3;
    static _STARTING_LIVES = 3;
    /** Seconds since the stage began (real time, paused while the
     *  game is paused, advanced by the speed multiplier so 4× also
     *  speeds up the in-world clock). */
    _elapsedSeconds = 0;
    /** In-world "wall clock" base, in minutes-since-midnight. The
     *  mockup shows 10:00, so we start there and add `_elapsedSeconds`
     *  scaled for a quick HUD readout. */
    static _CLOCK_START_MINUTES = 10 * 60; // 10:00 AM
    /** Real seconds per in-world minute — 5 s/min keeps the IDŐ
     *  clock visibly moving without sprinting through the hour. */
    static _CLOCK_RATE = 5;
    /** Soft mute toggle for the bottom-left music button. Wired
     *  through to nothing yet (the game has no audio system), but
     *  the button still flips state so the visual matches the
     *  mockup and we can plug in real audio later. */
    _muted = false;
    /** Track of trains we've already credited for reaching their
     *  goal — used so the score doesn't tick up every frame while
     *  a parked-on-goal train stays parked. */
    _scoredTrains = new Set();
    /** Track of trains we've already debited a life for — same
     *  reason, except we only ever lose one life per crash event. */
    _lostLifeTrains = new Set();

    /** Rolling buffer of past game snapshots (each captured every
     *  `_SNAPSHOT_INTERVAL` game-seconds) used to power the REWIND
     *  button. Newest at the end, oldest at index 0. We keep enough
     *  history to support one full rewind window plus a small
     *  cushion so the chosen snapshot is always available. */
    _snapshotHistory = [];
    /** How many REWINDs the player still has. The HUD shows this
     *  as "REWIND ×N" and disables the button at 0. */
    _rewindsRemaining = 5;
    /** Seconds-of-game-time between snapshots. Sub-second precision
     *  isn't worth the memory; the player just needs "a moment ago".
     *  Worst-case excess rewind distance is one INTERVAL — with 1 s
     *  and a 10 s target, a press lands between 10.0 s and 10.99 s
     *  of game-time back, which the player reads as a "clean 10 s". */
    static _SNAPSHOT_INTERVAL = 1.0;
    /** Target rewind distance, in seconds of game time. We pick the
     *  snapshot whose timestamp is closest to (now − this value)
     *  but never NEWER than the target — so a press always lands
     *  AT LEAST this many seconds in the past, never less. */
    static _REWIND_SECONDS = 10.0;
    /** Rewind distance for a WRONG-DESTINATION auto-rewind, in seconds
     *  of game time. Much longer than the collision rewind: a train
     *  that reached the wrong station may be far past the last junction
     *  with no manual reverses left, so we roll the sim back 30s to put
     *  it before a switch the player can flip. Comfortably inside the
     *  `_SNAPSHOT_HISTORY_MAX` (64s) buffer. */
    static _WRONG_DEST_REWIND_SECONDS = 30.0;
    /** Total REWINDs the player gets per playthrough. Reset by
     *  `_restartGame`. With `_REWIND_SECONDS = 10`, the maximum
     *  cumulative rewind budget is 5 × 10 s = 50 s of game time —
     *  matches the spec ("10 seconds × 5 times = real 50 seconds"). */
    static _REWIND_MAX = 5;
    /** Hard cap on history length. Must cover the cumulative rewind
     *  budget — every REWIND click slices the buffer down to the
     *  chosen target snapshot, so chaining N rewinds of K seconds
     *  needs (N × K) / INTERVAL entries in history. With the current
     *  5 rewinds × 10 s @ 1 s/snapshot that's 50 entries; we keep a
     *  generous cushion (64) on top so the closest-snapshot lookup
     *  always has a candidate strictly older than (now − REWIND_SECONDS)
     *  even if the player chains all five rewinds back-to-back. */
    static _SNAPSHOT_HISTORY_MAX = 64;
    /** Bookkeeping: elapsed-time of the last captured snapshot, so
     *  we know when to capture the next one. */
    _lastSnapshotAt = -1;
    /** Active rewind animation state, or null when the game is
     *  running normally. While non-null, the scene's `update()`
     *  short-circuits to `_tickRewindAnim` and the trains visually
     *  slide BACKWARDS along the snapshot timeline instead of the
     *  player getting an instant teleport. Cleared once the
     *  animation hits its target snapshot. */
    _rewindAnim = null;
    /** Real-time seconds the rewind animation takes — short enough
     *  to feel snappy ("VCR fast rewind") but long enough that the
     *  eye can follow each train moving backwards. */
    static _REWIND_ANIM_SECONDS = 0.55;

    _messageText = null;
    /** @type {TrainSimHud|null} */
    _domHud = null;
    _gameOverOverlay = null;
    _completeOverlay = null;
    _scoreOverlay = null;
    _chrome = null;
    _filterFx = null;

    /** Rounded-rect mask applied to the board container so cell content
     *  in the corners is clipped to a rounded shape — gives the game
     *  area the same rounded silhouette as the surrounding light-blue
     *  border ring. Lives in `_worldContainer` next to the board so it
     *  shares the same coordinate space. */
    _boardMask = null;
    /** Light-blue (#69b5f2) rounded-rect outline drawn on top of the
     *  board to frame the play area. Same shape as `_boardMask`. */
    _boardBorder = null;
    static _BOARD_BORDER_COLOR = 0x69b5f2;
    static _BOARD_BORDER_WIDTH = 4;
    static _BOARD_CORNER_RADIUS = 18;

    _onTap = null;
    _gridTapHandler = null;

    // Allow a stage payload (or filename) to be injected by the editor
    // for in-memory test-play without round-tripping through disk.
    pendingStage = null;

    init(game) {
        super.init(game);

        this._gameOver = false;
        this._allReachedGoal = false;
        this._paused = false;
        this._userPaused = false;
        this._chromePaused = false;
        this._trains = [];

        // Read player-facing assist settings once per init so the
        // HUD render path can decide whether to draw each tile.
        // Settings live in `trainsim.settings`; defaults come from
        // `data/trainsim.config.js`. Mid-game toggles in OptionsScene
        // take effect on the next stage restart (matches every other
        // trainsim setting).
        const getSet = (k, fallback) =>
            this.game.theme?.getSetting?.('trainsim.settings', k, fallback) ?? fallback;
        this._pauseEnabled  = !!getSet('pauseButtonEnabled', true);
        this._focusEnabled  = !!getSet('focusModeEnabled',   true);
        this._rewindEnabled = !!getSet('rewindEnabled',      true);

        // Restore the player's last chosen FOCUS state across stage
        // rebuilds. Without this, every restart / "AGAIN" resets to
        // bright mode, which is jarring for a player who deliberately
        // turned on focus mode for a tricky stage.
        if (typeof window !== 'undefined' && typeof window.__trainsimFocus === 'boolean') {
            this._focusMode = window.__trainsimFocus;
        } else {
            this._focusMode = false;
        }
        this._score = 0;
        this._lives = TrainSimScene._STARTING_LIVES;
        this._elapsedSeconds = 0;
        this._scoredTrains = new Set();
        this._lostLifeTrains = new Set();
        this._snapshotHistory = [];
        this._rewindsRemaining = TrainSimScene._REWIND_MAX;
        this._lastSnapshotAt = -1;
        this._rewindAnim = null;
        // Re-arm the distant-horn + track-sound ambience timers for
        // the fresh stage.
        this._nextHornIn = null;
        this._nextTrackIn = null;
        // Restore last-used speed so "AGAIN" / "RESTART" keeps the
        // player's chosen tempo instead of always reverting to 1×.
        if (typeof window !== 'undefined' && Number.isFinite(window.__trainsimSpeed)) {
            this._speedMultiplier = window.__trainsimSpeed;
        }

        this._createThemeBackground();
        this._worldContainer = new Container();
        this._hudContainer = new Container();
        this.container.addChild(this._worldContainer);
        this.container.addChild(this._hudContainer);

        this._createHud();
        this._chrome = new SceneChrome({
            game: this.game,
            host: this._hudContainer,
            overlayHost: this.container,
            gameId: 'trainsim',
            getHelp: () => this._buildHelp(),
            onBack: () => this._handleBack(),
            // SceneChrome's pause is a transient "modal is open"
            // freeze. It MUST NOT clobber the player's explicit
            // pause via the HUD tile — splitting into a separate
            // `_chromePaused` flag and recomputing `_paused` keeps
            // a deliberate user pause sticky across chrome modal
            // open/close cycles.
            onPause:  () => { this._chromePaused = true;  this._recomputePause(); },
            onResume: () => { this._chromePaused = false; this._recomputePause(); },
        });
        this._chrome.attach();
        // Help + leaderboard circles still live in the DOM HUD's small
        // round-button cluster (positioned BELOW the BACK button on
        // the right edge), so the canvas-space chrome copies would
        // just duplicate them — hide those two. The BACK button
        // KEEPS its canvas-space pill at the default top-RIGHT
        // position (game.width - 170, 55), with the DOM clock card
        // now living in the top-LEFT corner so the two never
        // overlap.
        if (this._chrome._helpBtn) this._chrome._helpBtn.visible = false;
        if (this._chrome._lbBtn)   this._chrome._lbBtn.visible   = false;
        // The HU/EN language toggle also lives in the DOM HUD's round-button
        // cluster (see TrainSimHud `_buildTrButtons`), so hide the canvas
        // pill the same way — the top-centre schedule strip leaves no room
        // for it on the canvas anyway.
        if (this._chrome._langBtn) this._chrome._langBtn.visible = false;

        this._filterFx = new FilterPipeline(this.game, this.container);

        // Stage loading. If the editor handed us a stage payload, use
        // it; otherwise fetch the default from disk.
        if (this.pendingStage) {
            const stage = this.pendingStage;
            this.pendingStage = null;
            this._buildStage(stage);
        } else {
            this._stageLoading = true;
            loadStage('trainsim', DEFAULT_STAGE_FILE).then((stage) => {
                this._stageLoading = false;
                if (!stage) {
                    this._showFatalMessage(`Could not load ${DEFAULT_STAGE_FILE}`);
                    return;
                }
                this._buildStage(stage);
            });
        }

        this._onTap = () => {
            // Tap-to-restart is only meaningful if the
            // GameOverOverlay is up. Crashes no longer trigger
            // game-over (they auto-rewind instead per client
            // feedback), so this branch is effectively dormant —
            // kept for any future "soft" game-over trigger.
            if (this._gameOver) this._restartGame();
        };
        this.game.input.on('tap', this._onTap);

        // Kick off the looping simulator ambience right away. If the
        // browser blocks autoplay it self-arms a one-shot gesture
        // retry, so the music comes up on the first interaction.
        this._startAmbience();
    }

    update(delta) {
        if (this._stageLoading) return;

        // The looping level-crossing bell is tied to trains held at a
        // red gate during LIVE play. Every branch below that freezes
        // the sim (pause, rewind, game over, all-arrived) skips
        // `tickLamps`, so silence the bell + gate pulses up-front to
        // avoid a stranded loop. Live play re-arms it via tickLamps.
        if (this._paused || this._gameOver || this._dangerRewindActive
            || this._allReachedGoal || this._rewindAnim) {
            this._board?.silenceCrossingBell?.();
        }

        if (this._paused) return;
        if (this._gameOver) {
            this._gameOverOverlay?.update?.(delta);
            return;
        }
        // Danger auto-rewind: short highlight pulse, then the
        // existing rewind animation. We tick BOTH phases here so
        // pause/gameOver checks above still gate the whole flow.
        if (this._dangerRewindActive) {
            this._tickDangerRewind(delta);
            if (this._domHud) this._domHud.refresh();
            if (this._filterFx) this._filterFx.update(delta);
            return;
        }
        if (this._allReachedGoal) {
            this._completeOverlay?.update?.(delta);
            return;
        }
        if (!this._board) return;

        // REWIND animation owns the frame while it's running — we
        // freeze the normal sim (cars / switches / trains) and
        // slide every train sprite backwards along the pre-computed
        // snapshot timeline. The actual gameplay
        // state isn't restored until the animation lands on its
        // target frame, so the visible rewind always matches the
        // settled-back state the player sees afterwards.
        if (this._rewindAnim) {
            this._tickRewindAnim(delta);
            if (this._domHud) this._domHud.refresh();
            if (this._filterFx) this._filterFx.update(delta);
            return;
        }

        const api = this._board.getBoardApi();
        let allDone = true;

        // Tick the post-rewind grace counter every live frame so
        // the proactive path-overlap detector can re-arm once
        // enough time has passed for the player to react.
        if (this._dangerCooldownFrames > 0) {
            this._dangerCooldownFrames -= delta;
            if (this._dangerCooldownFrames < 0) this._dangerCooldownFrames = 0;
        }

        // Publish the live train list to the board so its
        // `isCellOccupiedByOther` last-mile collision guard can
        // scan every alive train. Cheap pointer assignment — no
        // copying, no per-train work.
        if (typeof this._board.setLiveTrains === 'function') {
            this._board.setLiveTrains(this._trains);
        }

        // Road traffic — runs at the same speed multiplier as trains
        // so "fast forward" speeds up the whole world together.
        if (typeof this._board.tickCars === 'function') {
            this._board.tickCars(delta * (this._speedMultiplier || 1));
        }

        // Switch animations (pulsing haloes + post-click flashes).
        // Runs at REAL time, not the speed-mult time, so the visual
        // feedback feels instantaneous regardless of how fast the
        // sim is running. Cheap no-op when there are no switches.
        if (typeof this._board.tickSwitches === 'function') {
            this._board.tickSwitches(delta);
        }

        // Lamp animations: bulb blink (red lamps flash white↔red
        // ~1.6 Hz so the player can't miss the STOP signal), halo
        // breath and barrier-arm tween (open on GREEN, closed on
        // RED). Real-time tick so the strobe stays consistent
        // regardless of speed multiplier.
        if (typeof this._board.tickLamps === 'function') {
            this._board.tickLamps(delta);
        }

        // Cloud drift — every cloud sprite slides slowly rightward,
        // each at its own pace, and wraps off-screen for a seamless
        // sky. Real-time tick so the weather looks identical at 1×
        // and 4× sim tempo.
        if (typeof this._board.tickClouds === 'function') {
            this._board.tickClouds(delta);
        }

        // Wind-blown leaves drifting across the board (opt-in via the
        // `windLeavesEnabled` setting; cheap no-op when off). Same
        // direction as the clouds so the wind cue is coherent map-
        // wide. Real-time so the breeze stays consistent across sim
        // tempo just like the clouds.
        if (typeof this._board.tickLeaves === 'function') {
            this._board.tickLeaves(delta);
        }

        // Foliage sway — trees, bushes and flowerbeds wobble gently at
        // their base, each with its own phase so the canopy never
        // moves in lockstep. Same toggle as the leaves so the
        // settings flag controls all wind cues consistently.
        if (typeof this._board.tickWobble === 'function') {
            this._board.tickWobble(delta);
        }

        // Route-highlight pulse (player tapped a spawn/goal): runs at
        // real time, not the speed-mult time, so the "this is your
        // train" cue feels instant regardless of game tempo.
        if (typeof this._board.tickRouteHighlight === 'function') {
            this._board.tickRouteHighlight(delta);
        }

        // "Wrong destination" warning pulse: throbs the red route +
        // warning triangle on any train predicted to land at the
        // wrong station, so the player notices BEFORE the crash.
        if (typeof this._board.tickPathOverlayPulse === 'function') {
            this._board.tickPathOverlayPulse(delta);
        }

        // Confetti shower on successful arrivals — runs at real time
        // so the celebration timing is identical regardless of game
        // speed (cheap no-op when no particles are alive).
        if (typeof this._board.tickGoalConfetti === 'function') {
            this._board.tickGoalConfetti(delta);
        }

        // Scale the per-frame delta by the player's chosen tempo
        // (1× / 2× / 4× from the HUD speed button). Only the train
        // update gets the scaled delta — overlays, animations and
        // filters continue at real time so transitions don't blur.
        const trainDelta = delta * (this._speedMultiplier || 1);

        // Advance the in-world clock at the same tempo as the trains
        // (paused frames don't reach here; speed-multiplied frames
        // advance proportionally). 1 frame = 1/60 s × multiplier.
        this._elapsedSeconds += (trainDelta / 60);

        // Capture a rewind-history snapshot every _SNAPSHOT_INTERVAL
        // game-seconds so the REWIND button always has something
        // recent to jump back to. We snapshot BEFORE the per-train
        // update, so a rewind lands on a clean tick boundary that
        // already reflects every render-step the player saw — no
        // half-applied animation states.
        this._maybeTakeSnapshot();

        for (const train of this._trains) {
            const wasReached = train._reachedGoal;
            const result = train.update(trainDelta, api);
            train.render();
            // Departure announcer: when a waiting train enters its
            // final 10-second pre-launch window, fire the audio
            // bell ONCE per train. The HUD picks up the same
            // `_isBoarding` flag and pulses the matching menetrend
            // row so the player gets coordinated audio + visual
            // lead-time on each launch.
            if (train._isBoarding && !train._announcedDeparture) {
                train._announcedDeparture = true;
                this._playDepartureBell();
            }
            // Beep countdown — when the train enters its final 4
            // seconds, fire `countdown.mp3` ONCE. The clip beeps
            // 4 times (4·3·2·1) in sync with the on-board 4·3·2·1
            // number; the train departs on the "0".
            if (train._isCountingDown && !train._countdownPlayed) {
                train._countdownPlayed = true;
                this._playCountdownSound();
            }
            if (!result.alive) {
                // Per client feedback: collisions / derailments /
                // wrong-destinations are no longer fatal. Trigger
                // the danger auto-rewind flow — pulse the offender,
                // show the centred Hungarian banner, and roll the
                // sim back ~10 seconds so the player can re-attempt
                // the dangerous moment instead of losing a life.
                // Rewind restores the snapshot's `_alive` state, so
                // setting it to false here is harmless: it gets
                // reverted along with everything else. Pass the crash
                // reason so a wrong-destination arrival gets its own
                // banner + the longer 30s rewind window.
                this._triggerDangerRewind([train], { reason: result.crashReason });
                return;
            }
            if (!result.reachedGoal) {
                allDone = false;
            } else {
                const justArrived = !wasReached;
                this._registerGoal(train);
                if (justArrived && this._board?.setTrainPathVisible) {
                    // Train just crossed its finish line — hide its
                    // predicted-path overlay so completed routes don't
                    // keep cluttering the board for the player who's
                    // still routing the other trains.
                    this._board.setTrainPathVisible(train.trainId, false);
                    // Celebrate the safe arrival — three pieces:
                    //   1. A big confetti burst on the goal cell.
                    //   2. A floating "<ID> HAS ARRIVED!" banner above
                    //      the same cell so the achievement reads
                    //      from across the board.
                    //   3. The train itself fades out + hides (handled
                    //      inside TrainSimTrain.update via the
                    //      `_arrivalFadeTimer` field) so the goal cell
                    //      doesn't stay permanently occupied by a
                    //      stationary tram.
                    // LOCALISED fireworks anchored on the goal cell —
                    // a short staggered "bom-bom-bom" volley of in-board
                    // confetti bursts (rendered in the game's own canvas
                    // RIGHT on the destination, NOT a screen-wide shower),
                    // each paired with the win-screen pop sound. Stays
                    // small + around the station the train just reached.
                    if (typeof this._board.spawnGoalConfetti === 'function') {
                        const VOLLEY = 4;        // bursts
                        const VOLLEY_GAP_MS = 140;
                        const tid = train.trainId;
                        for (let k = 0; k < VOLLEY; k++) {
                            setTimeout(() => {
                                if (!this._board) return;
                                this._board.spawnGoalConfetti(tid, { big: true });
                                confettiPop();
                            }, k * VOLLEY_GAP_MS);
                        }
                    }
                    // Arrival jingle — fresh element per call so trains
                    // arriving back-to-back don't cut each other off.
                    this._playArrivalSound();
                    if (typeof this._board.showArrivalBanner === 'function') {
                        // Pass the train's readable destination name so
                        // the banner announces e.g. "Miskolc-Tiszai
                        // vonat" instead of the cryptic id "T1".
                        this._board.showArrivalBanner(train.trainId, train.destinationCity);
                    }
                    // Persistent crowd of greeters at the goal station —
                    // one large figure + several smaller ones, scattered
                    // around the cell. Calls accumulate across the
                    // stage so multiple deliveries to the same station
                    // build up a visible crowd. Cleared on stage reset.
                    if (typeof this._board.addArrivedCrowd === 'function') {
                        this._board.addArrivedCrowd(train.trainId);
                    }
                    const arrivedTpl = t('trainsim.goal_reached')
                        || 'Train {id} arrived safely!';
                    // Prefer the readable destination name ("Miskolc-
                    // Tiszai") over the cryptic id ("T1") here too, so
                    // the toast matches the arrival banner.
                    const arrivedName = (train.destinationCity || '').trim()
                        || train.trainId.toUpperCase();
                    this._domHud?.showToast?.(
                        arrivedTpl.replace('{id}', arrivedName),
                        'success',
                        2800,
                    );
                }
            }
        }

        // Pool-game-style aim line: redraw a short solid-magenta
        // "next 5 cells" forecast in front of every live, moving
        // train. We run this AFTER the per-train update + render
        // step so the dots can anchor to the head sprite's freshly
        // smoothed pixel position — the result slides cleanly with
        // the train instead of snapping cell-to-cell.
        if (typeof this._board.tickAimLines === 'function') {
            this._board.tickAimLines(this._trains);
        }

        // Occasional distant-train-horn ambience — counts down on
        // game time and only fires while a train is actually moving.
        this._tickAmbientHorn(trainDelta / 60);
        // Occasional rolling-over-the-tracks ambience — same gating.
        this._tickTrackSound(trainDelta / 60);

        // Proactive danger detection (per client feedback): if
        // any two trains' predicted 5-cell paths share a cell
        // — i.e. the magenta dotted lines visibly touch — fire
        // the danger banner + auto-rewind NOW, well before the
        // trains physically meet. We run this BEFORE the
        // post-step `_detectCellCollision` so the player gets
        // the warning at the earliest possible moment.
        if (this._detectImpendingPathCollision()) return;

        // Backstop: same-cell collision detection for the rare
        // case where two trains DID land on the same cell on the
        // same tick (e.g. both spawned simultaneously on top of
        // each other). The proactive check above usually fires
        // first, but this guard remains as a belt-and-braces
        // safety net.
        if (this._detectCellCollision()) return;

        if (allDone && this._trains.length > 0) {
            this._onAllReachedGoal();
            return;
        }

        this._updateHud();
        if (this._domHud) this._domHud.refresh();
        if (this._filterFx) this._filterFx.update(delta);
    }

    /**
     * Fire the "train is about to depart" audio cue. Called once
     * per train, exactly when its boarding window opens (10 s
     * before the schedule lets it pull out of the station).
     *
     * Uses `assets/simulator/sounds/mav_szignal.mp3` — the official
     * MÁV (Hungarian Railways) station signal the client supplied.
     * Cached after the first play so back-to-back announcements
     * re-trigger from the start without spawning N audio elements.
     *
     * If the file is missing or the browser blocks the play promise
     * (e.g. autoplay policy before any user gesture), we fall back
     * to a synthesised two-tone bell so the cue is never silent.
     * Both paths are best-effort: any error is swallowed so audio
     * issues never break gameplay.
     *
     * To swap the chime, just drop a new mp3/ogg/wav at the same
     * path; no code change needed.
     */
    _playDepartureBell() {
        const url = 'assets/simulator/sounds/mav_szignal.mp3';
        try {
            // Cache one HTMLAudioElement per scene instance — fresh
            // .currentTime + .play() lets back-to-back announcements
            // re-trigger from the start without spawning N elements.
            if (!this._departureBellEl) {
                this._departureBellEl = new Audio(url);
                this._departureBellEl.preload = 'auto';
                this._departureBellEl.volume = 0.85;
            }
            const el = this._departureBellEl;
            el.currentTime = 0;
            const p = el.play();
            if (p && typeof p.catch === 'function') {
                p.catch(() => this._playSynthBell());
            }
            return;
        } catch (_) { /* fall through */ }
        this._playSynthBell();
    }

    /**
     * Two-tone "ding-ding" bell synthesized via Web Audio API.
     * Standalone fallback for `_playDepartureBell` when no MP3
     * asset is available. ~700 ms total, A5+E5 sine + decay.
     */
    _playSynthBell() {
        try {
            const Ctor = window.AudioContext || window.webkitAudioContext;
            if (!Ctor) return;
            if (!this._audioCtx) this._audioCtx = new Ctor();
            const ctx = this._audioCtx;
            const t0 = ctx.currentTime;
            const tones = [
                { f: 880, t: 0.00 }, // A5
                { f: 659, t: 0.18 }, // E5
            ];
            for (const tone of tones) {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(tone.f, t0 + tone.t);
                gain.gain.setValueAtTime(0, t0 + tone.t);
                gain.gain.linearRampToValueAtTime(0.18, t0 + tone.t + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, t0 + tone.t + 0.55);
                osc.connect(gain).connect(ctx.destination);
                osc.start(t0 + tone.t);
                osc.stop(t0 + tone.t + 0.6);
            }
        } catch (_) { /* silent */ }
    }

    /**
     * Play the train-reverse cue. Fired both when the player hits
     * the VISSZA (rewind) button and when the proactive collision
     * detector auto-reverses the sim to undo an impending crash —
     * the two moments the trains visibly roll backwards, so they
     * share one sound.
     *
     * Uses `assets/simulator/sounds/train-reverse.mp3`, cached on
     * the scene so back-to-back rewinds re-trigger from the start.
     * Best-effort: any error (missing file, autoplay block) is
     * swallowed so a denied/throttled play never disrupts the
     * rewind flow.
     */
    _playReverseSound() {
        try {
            if (!this._reverseSfxEl) {
                this._reverseSfxEl = new Audio('assets/simulator/sounds/train-reverse.mp3');
                this._reverseSfxEl.preload = 'auto';
                this._reverseSfxEl.volume = 0.9;
            }
            const el = this._reverseSfxEl;
            el.currentTime = 0;
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (_) { /* silent */ }
    }

    /**
     * Play the 4-beep departure countdown (`countdown.mp3`). Fired
     * once per train when it enters its final 4 seconds; the clip's
     * four beeps line up with the on-board 4·3·2·1 number and the
     * train pulls out on "0". Cached + best-effort like the other
     * cues.
     */
    _playCountdownSound() {
        try {
            if (!this._countdownSfxEl) {
                this._countdownSfxEl = new Audio('assets/simulator/sounds/countdown.mp3');
                this._countdownSfxEl.preload = 'auto';
                this._countdownSfxEl.volume = 0.85;
            }
            const el = this._countdownSfxEl;
            el.currentTime = 0;
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (_) { /* silent */ }
    }

    /**
     * Occasional "distant train horn" ambience. Called every live
     * frame with the game-time delta; it only counts down (and can
     * only fire) while at least one train is actually moving on the
     * board, so a stage sitting on the start line stays quiet. When
     * the timer elapses it plays `distant-train-horn.mp3` softly and
     * re-rolls a fresh, fairly long random interval so the horn
     * stays a rare, atmospheric touch rather than a constant noise.
     *
     * @param {number} dtSec game-time seconds elapsed this frame
     */
    _tickAmbientHorn(dtSec) {
        const anyMoving = this._trains.some(
            (tr) => tr._started && tr._alive && !tr._reachedGoal && !tr.paused,
        );
        if (!anyMoving) return;
        // Lazily arm the first interval the moment a train starts
        // rolling — random so it never lands at a predictable beat.
        if (this._nextHornIn == null) {
            this._nextHornIn = this._rollHornInterval();
        }
        this._nextHornIn -= dtSec;
        if (this._nextHornIn > 0) return;
        this._nextHornIn = this._rollHornInterval();
        try {
            if (!this._hornSfxEl) {
                this._hornSfxEl = new Audio('assets/simulator/sounds/distant-train-horn.mp3');
                this._hornSfxEl.preload = 'auto';
                this._hornSfxEl.volume = 0.45; // "distant" → quiet
            }
            const el = this._hornSfxEl;
            el.currentTime = 0;
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (_) { /* silent */ }
    }

    /** Random gap (game-seconds) between two distant-horn ambience plays. */
    _rollHornInterval() {
        return 36 + Math.random() * 36; // 36–72 s (rarer than before)
    }

    /**
     * Occasional "train rolling over the tracks" ambience — same
     * shape as {@link _tickAmbientHorn}: counts down on game time and
     * only fires while at least one train is actually moving, so a
     * stage sitting on the start line stays silent. When the timer
     * elapses it plays `train-track-sound.mp3` and re-rolls a fresh
     * random interval so it stays an occasional atmospheric touch.
     *
     * @param {number} dtSec game-time seconds elapsed this frame
     */
    _tickTrackSound(dtSec) {
        const anyMoving = this._trains.some(
            (tr) => tr._started && tr._alive && !tr._reachedGoal && !tr.paused,
        );
        if (!anyMoving) return;
        if (this._nextTrackIn == null) {
            this._nextTrackIn = this._rollTrackInterval();
        }
        this._nextTrackIn -= dtSec;
        if (this._nextTrackIn > 0) return;
        this._nextTrackIn = this._rollTrackInterval();
        try {
            if (!this._trackSfxEl) {
                this._trackSfxEl = new Audio('assets/simulator/sounds/train-track-sound.mp3');
                this._trackSfxEl.preload = 'auto';
                this._trackSfxEl.volume = 0.4;
            }
            const el = this._trackSfxEl;
            el.currentTime = 0;
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (_) { /* silent */ }
    }

    /** Random gap (game-seconds) between two track-sound ambience plays. */
    _rollTrackInterval() {
        return 7 + Math.random() * 10; // 7–17 s (more frequent than before)
    }

    /**
     * Start the looping background ambience for the simulator.
     * Called once from `init()` so the soundscape comes up the
     * moment the stage scene mounts.
     *
     * Two looping layers:
     *   • `background-ambience.mp3` at 0.32 — the base soundscape
     *     that sits UNDER the MÁV departure signal.
     *   • `wind-ambiernce.mp3` at 0.40 — a breezy wind layer over
     *     the top (filename intentionally matches the asset on disk).
     *
     * Autoplay policy: most browsers refuse to start audio before
     * the user has interacted with the page. If an initial
     * `play()` promise rejects, we arm a one-shot gesture listener
     * (pointer / key / touch) that retries BOTH loops on the very
     * first interaction, then removes itself. Idempotent — calling
     * it again while the loops are already alive is a no-op.
     */
    _startAmbience() {
        if (this._ambienceEl) return;
        try {
            const el = new Audio('assets/simulator/sounds/background-ambience.mp3');
            el.loop = true;
            el.preload = 'auto';
            el.volume = 0.32;
            this._ambienceEl = el;

            const wind = new Audio('assets/simulator/sounds/wind-ambiernce.mp3');
            wind.loop = true;
            wind.preload = 'auto';
            wind.volume = 0.40;
            this._windAmbienceEl = wind;

            const tryPlay = () => {
                for (const audio of [this._ambienceEl, this._windAmbienceEl]) {
                    if (!audio) continue;
                    const p = audio.play();
                    if (p && typeof p.catch === 'function') {
                        p.catch(() => this._armAmbienceGesture());
                    }
                }
            };
            tryPlay();
        } catch (_) {
            this._armAmbienceGesture();
        }
    }

    /**
     * Retry ambience playback on the first user gesture, working
     * around the browser autoplay block. The listener detaches
     * itself after one successful (or attempted) play so it never
     * fires twice.
     */
    _armAmbienceGesture() {
        if (typeof window === 'undefined' || this._ambienceGestureHandler) return;
        const handler = () => {
            this._disarmAmbienceGesture();
            try { this._ambienceEl?.play?.().catch(() => {}); } catch (_) {}
            try { this._windAmbienceEl?.play?.().catch(() => {}); } catch (_) {}
        };
        this._ambienceGestureHandler = handler;
        window.addEventListener('pointerdown', handler, { once: true });
        window.addEventListener('keydown', handler, { once: true });
        window.addEventListener('touchstart', handler, { once: true });
    }

    _disarmAmbienceGesture() {
        if (typeof window === 'undefined' || !this._ambienceGestureHandler) return;
        const handler = this._ambienceGestureHandler;
        window.removeEventListener('pointerdown', handler);
        window.removeEventListener('keydown', handler);
        window.removeEventListener('touchstart', handler);
        this._ambienceGestureHandler = null;
    }

    destroy() {
        const input = this.game?.input;
        if (input && this._onTap) input.off('tap', this._onTap);

        const hitArea = this._board?.getHitArea();
        if (hitArea && this._gridTapHandler) hitArea.off('pointertap', this._gridTapHandler);

        if (this._filterFx) { this._filterFx.destroy(); this._filterFx = null; }
        if (this._chrome) { this._chrome.destroy(); this._chrome = null; }
        if (this._domHud) { this._domHud.destroy(); this._domHud = null; }
        if (this._completeOverlay) { this._completeOverlay.destroy?.(); this._completeOverlay = null; }
        if (this._gameOverOverlay) { this._gameOverOverlay.destroy?.(); this._gameOverOverlay = null; }
        if (this._scoreOverlay) { this._scoreOverlay.destroy?.(); this._scoreOverlay = null; }
        for (const train of this._trains) train.destroy();
        this._trains = [];
        this._removeBoardFrame();
        if (this._board) { this._board.destroy(); this._board = null; }
        // Release any audio handles spun up by the departure-bell
        // announcer so they don't keep buffers alive after the
        // scene unmounts.
        if (this._departureBellEl) {
            try { this._departureBellEl.pause(); } catch (_) {}
            this._departureBellEl = null;
        }
        if (this._reverseSfxEl) {
            try { this._reverseSfxEl.pause(); } catch (_) {}
            this._reverseSfxEl = null;
        }
        if (this._countdownSfxEl) {
            try { this._countdownSfxEl.pause(); } catch (_) {}
            this._countdownSfxEl = null;
        }
        if (this._hornSfxEl) {
            try { this._hornSfxEl.pause(); } catch (_) {}
            this._hornSfxEl = null;
        }
        if (this._trackSfxEl) {
            try { this._trackSfxEl.pause(); } catch (_) {}
            this._trackSfxEl = null;
        }
        if (this._finishedSfxEl) {
            try { this._finishedSfxEl.pause(); } catch (_) {}
            this._finishedSfxEl = null;
        }
        if (this._arrivalSfxEl) {
            try { this._arrivalSfxEl.pause(); } catch (_) {}
            this._arrivalSfxEl = null;
        }

        this._nextHornIn = null;
        this._nextTrackIn = null;
        if (this._audioCtx) {
            try { this._audioCtx.close?.(); } catch (_) {}
            this._audioCtx = null;
        }
        // Stop + release the looping background ambience and drop
        // any pending autoplay-retry gesture listener.
        this._disarmAmbienceGesture();
        if (this._ambienceEl) {
            try { this._ambienceEl.pause(); } catch (_) {}
            this._ambienceEl = null;
        }
        if (this._windAmbienceEl) {
            try { this._windAmbienceEl.pause(); } catch (_) {}
            this._windAmbienceEl = null;
        }
        this._onTap = null;
        this._gridTapHandler = null;
        this._paused = false;
        this._userPaused = false;
        this._chromePaused = false;
        // Make sure a freshly-restarted scene doesn't inherit an
        // armed danger-rewind from the previous instance — the
        // HUD destroy already removed the banner DOM, but the
        // flags would otherwise persist on the recycled scene.
        this._dangerRewindActive = false;
        this._dangerHighlightFrames = 0;
        this._dangerHighlightTrains = [];
        this._dangerCooldownFrames = 0;
        this._dangerRewindSeconds = 0;
        super.destroy();
    }

    // ── setup ──────────────────────────────────────────

    _buildStage(stage) {
        TrainSimScene._migrateLegacyDirs(stage);
        this._stage = stage;
        this._board = new TrainSimBoard(this.game.theme);
        this._board.init(stage, 'track');
        this._worldContainer.addChild(this._board.container);

        // Position + scale the board so the DOM HUD (schedule strip
        // along the top, switches / lamps / legend along the bottom)
        // never covers the play area. We reserve vertical space for
        // those HUD bands and fit the entire grid into the remaining
        // window, scaling DOWN if needed (never up) and centring the
        // result. A stage that naturally fits without scaling stays
        // at 1:1.
        const cs = stage.grid?.cellSize ?? TRAINSIM_GRID.cellSize;
        const cols = stage.grid?.cols ?? TRAINSIM_GRID.cols;
        const rows = stage.grid?.rows ?? TRAINSIM_GRID.rows;
        const gridW = cols * cs;
        const gridH = rows * cs;

        // Board nearly fills the 1920 × 1080 canvas, leaving a tiny
        // 4-px breathing room on each side so the rounded #69b5f2
        // border draws fully on-screen instead of getting clipped at
        // the canvas edges. The HUD (schedule, stat cards, clock,
        // bottom tile row) is z-indexed above the board and overlaps
        // the centre of the play area, matching the reference mockup.
        const SIDE_INSET = 4;
        const availW = this.game.width - SIDE_INSET * 2;
        const availH = this.game.height;
        const fit = Math.min(availW / gridW, availH / gridH);

        this._board.container.scale.set(fit);
        const scaledW = gridW * fit;
        const scaledH = gridH * fit;
        const ox = Math.round((this.game.width - scaledW) / 2);
        const oy = Math.round((this.game.height - scaledH) / 2);
        this._board.container.x = ox;
        this._board.container.y = oy;

        this._installBoardFrame(ox, oy, scaledW, scaledH);

        // Wire input on the board: taps on lamps/switches toggle them.
        this._gridTapHandler = (e) => {
            if (this._gameOver || this._allReachedGoal) return;
            const local = e.getLocalPosition(this._board.container);
            const hit = this._board.hitTestEntity(local.x, local.y);
            if (!hit) return;
            // A switch carries two on-board controls: tapping its
            // signal head toggles stop/go, tapping the rest of the
            // cell cycles its routing.
            if (hit.kind === 'switch-signal') this._board.toggleSwitchSignal(hit.entity);
            if (hit.kind === 'switch')        this._board.toggleSwitch(hit.entity);
            // Tapping a spawn or goal isn't a toggle — it's the
            // player's "show me where this train goes" gesture. Flash
            // both endpoints of the matching train so the otherwise
            // homogeneous station artwork resolves into a clear
            // FROM → TO pair.
            if (hit.kind === 'spawn' || hit.kind === 'goal') {
                const tid = hit.entity?.props?.trainId;
                if (tid && typeof this._board.flashTrainRoute === 'function') {
                    this._board.flashTrainRoute(tid);
                }
            }
            // Tapping the HONNAN / HOVA badge on a station info card.
            // Routes through the board's specialised endpoint flash so
            // the matching origin / destination cell yoyos AND the
            // tapped badge press-pulses, giving directional feedback.
            if (hit.kind === 'info-card') {
                const tid = hit.entity?._cardTrainId
                         || hit.entity?.props?.trainId;
                if (tid && typeof this._board.flashStationEndpoint === 'function') {
                    this._board.flashStationEndpoint(tid, hit.which, hit.entity);
                }
            }
        };
        const hitArea = this._board.getHitArea();
        hitArea.on('pointertap', this._gridTapHandler);

        this._createTrains(cs);
        // Randomize every per-switch SIGNAL (the train stop/go control)
        // to a fresh red/green mix so the player doesn't open every
        // game with every signal in the same state. Barriers are
        // automatic + car-only now, so there's nothing else to seed.
        this._board.randomizeSwitchSignals();
        // Apply the restored / default FOCUS mode to the fresh board.
        // Safe to call unconditionally — `setFocusMode(false)` is a
        // cheap no-op when the overlay is already hidden, and we want
        // a true value to surface the dim immediately on re-init
        // (e.g. after a game-over → AGAIN with focus engaged).
        if (typeof this._board.setFocusMode === 'function') {
            this._board.setFocusMode(this._focusMode);
        }
        this._updateHud();
        this._mountDomHud();
    }

    /**
     * Wrap the play area in a rounded-rect frame:
     *   • a soft 18-px corner mask on the board container so cells in
     *     the corners are clipped to the rounded silhouette, and
     *   • a 4-px #69b5f2 outline drawn on top so the frame reads as a
     *     deliberate light-blue ring around the map.
     *
     * Both live in `_worldContainer` next to the board so they share
     * the same coordinate space. Coordinates are passed in CANVAS-
     * SPACE (the post-scale screen position of the board) because
     * masks and the border don't ride along with the board's
     * `scale.set(fit)` — they're sized directly in screen pixels.
     */
    _installBoardFrame(ox, oy, w, h) {
        this._removeBoardFrame();
        if (w <= 0 || h <= 0) return;
        const radius = TrainSimScene._BOARD_CORNER_RADIUS;
        const stroke = TrainSimScene._BOARD_BORDER_WIDTH;
        const color  = TrainSimScene._BOARD_BORDER_COLOR;

        const mask = new Graphics();
        mask.roundRect(ox, oy, w, h, radius).fill(0xffffff);
        this._worldContainer.addChild(mask);
        this._board.container.mask = mask;
        this._boardMask = mask;

        const border = new Graphics();
        const inset = stroke / 2;
        border
            .roundRect(ox - inset, oy - inset, w + stroke, h + stroke, radius + inset)
            .stroke({ width: stroke, color, alignment: 0.5 });
        this._worldContainer.addChild(border);
        this._boardBorder = border;
    }

    _removeBoardFrame() {
        if (this._board?.container) this._board.container.mask = null;
        if (this._boardMask) {
            if (this._boardMask.parent) this._boardMask.parent.removeChild(this._boardMask);
            this._boardMask.destroy?.();
            this._boardMask = null;
        }
        if (this._boardBorder) {
            if (this._boardBorder.parent) this._boardBorder.parent.removeChild(this._boardBorder);
            this._boardBorder.destroy?.();
            this._boardBorder = null;
        }
    }

    _mountDomHud() {
        if (this._domHud) {
            this._domHud.destroy();
            this._domHud = null;
        }
        if (!this._board || this._trains.length === 0) return;
        this._domHud = new TrainSimHud({
            board: this._board,
            trains: this._trains,
            themeManager: this.game.theme,
            onRestart: () => this._restartGame(),
            // REWIND button — jumps the whole sim back
            // `_REWIND_SECONDS` of game time, up to `_REWIND_MAX`
            // uses per playthrough. Conditionally wired: when the
            // `rewindEnabled` Options toggle is off, we pass `null`
            // so the HUD skips drawing the tile entirely (the
            // existing `if (this._onRewind)` gate in `_buildBottomRow`
            // handles the disappear cleanly).
            onRewind: this._rewindEnabled ? (() => this._rewindGame()) : null,
            getRewindsRemaining: () => this._rewindsRemaining,
            getRewindMax:        () => TrainSimScene._REWIND_MAX,
            // PAUSE button — same gating pattern: only wired when
            // the `pauseButtonEnabled` Options toggle is on.
            onPause:    this._pauseEnabled ? (() => this._togglePause())   : null,
            getPaused:  this._pauseEnabled ? (() => !!this._userPaused)    : null,
            // FOCUS button — same gating.
            onFocus:    this._focusEnabled ? (() => this._toggleFocus())   : null,
            getFocused: this._focusEnabled ? (() => !!this._focusMode)     : null,
            getSpeed: () => this._speedMultiplier,
            cycleSpeed: () => this._cycleSpeed(),
            speedSteps: TrainSimScene._SPEED_STEPS,
            // Live counters for the top-left score/lives cards and
            // the top-right clock. Polled every frame by the HUD's
            // refresh() — no need to push from here.
            getScore: () => this._score,
            getLives: () => this._lives,
            getMaxLives: () => TrainSimScene._STARTING_LIVES,
            getClockText: () => this._formatClock(),
            // Bottom-left music toggle. The game has no audio
            // system yet, but flipping `_muted` keeps the
            // interaction wired so we can hook real audio in
            // later without touching the HUD.
            getMuted: () => this._muted,
            toggleMuted: () => { this._muted = !this._muted; return this._muted; },
            // Help + leaderboard buttons that replace the
            // hidden Pixi chrome — when clicked, they re-open
            // the same overlays the chrome would have.
            onOpenHelp:        () => this._chrome?._showHelp?.(),
            onOpenLeaderboard: () => this._chrome?._showLeaderboard?.(),
            onBack:            () => this._handleBack(),
            // HU/EN toggle: flip the locale, persist it, then re-init the
            // scene so every `t()` label (and the DOM HUD) re-renders in
            // the new language — same behaviour as the other games' pills.
            onToggleLang:      () => {
                const newLang = toggleLang();
                this.game.theme?.setLang?.(newLang);
                this.game.sceneManager.switchScene('trainsim');
            },
        });
        this._domHud.mount();
    }

    /**
     * Format the player's real wall-clock time as "HH:MM" for the
     * IDŐ card. Uses the browser's local timezone (whatever the OS
     * is set to) so the displayed clock matches whatever clock the
     * player can see on their device.
     *
     * We deliberately ignore `_elapsedSeconds` and the in-world
     * 10:00 start — the user wants the actual time of day, not a
     * simulated clock — but the elapsed counter is still maintained
     * elsewhere because the speed-multiplier and scheduling logic
     * rely on it.
     */
    _formatClock() {
        const now = new Date();
        const hh = now.getHours();
        const mm = now.getMinutes();
        return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    }

    _createTrains(cellSize) {
        const gameplayLayer = this._board.getGameplayLayer();
        if (!gameplayLayer) {
            console.warn('[TrainSimScene] No gameplay layer in stage!');
            return;
        }

        // ── Randomised departure schedule ─────────────────
        //
        // Before, every train left the moment the stage loaded —
        // five locomotives crashing into each other instantly.
        // We now assign each spawn a UNIQUE random start delay
        // drawn from a pool of evenly-spaced slots so:
        //   • no two trains leave at exactly the same second,
        //   • the spread reads as a believable timetable
        //     (≈ 0..40 seconds = 0..8 in-world minutes given the
        //     5-sec-per-in-world-minute clock), and
        //   • a restart re-rolls the order so the puzzle feels
        //     fresh every replay.
        //
        // The schedule overrides whatever `props.startDelay` the
        // designer happened to set in the editor — the player's
        // request was explicit ("must start at a different time
        // randomly"). Editors can still leverage start delays at
        // design time; they just don't apply in live play.
        //
        // GLOBAL OFFSET: every slot gets `startDelaySeconds` (Options
        // page, default 30 s) added on top so the player has a fixed
        // planning window after the stage opens before ANY train
        // starts moving. Setting it to 0 reverts to the previous
        // "first train leaves immediately" behaviour.
        //
        // SLOT_STEP_SEC: 60-second cadence between consecutive
        // trains, per client direction — only the first train
        // departs at the global delay; train 2 leaves a full
        // minute later, train 3 a minute after that, etc. The
        // 10-second pre-departure window (audio bell + flashing
        // menetrend row) lives inside this minute so the player
        // gets clear lead-time on each launch. The slot order is
        // still randomised below so a restart re-rolls which
        // train is "first / second / …" each time.
        const globalDelay = Math.max(0, Number(
            this.game.theme?.getSetting?.('trainsim', 'startDelaySeconds', 30)
        ) || 0);
        const SLOT_STEP_SEC = 60;
        const slots = this._board.spawns.map((_, i) => globalDelay + i * SLOT_STEP_SEC);
        // Fisher-Yates shuffle for a uniform random permutation.
        for (let i = slots.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [slots[i], slots[j]] = [slots[j], slots[i]];
        }

        this._board.spawns.forEach((spawn, idx) => {
            if (!spawn.props) spawn.props = {};
            spawn.props.startDelay = slots[idx];
        });

        for (const spawn of this._board.spawns) {
            const trainId = spawn.props?.trainId || 't1';
            const goal = this._board.goals.find((g) => g.props?.trainId === trainId);
            if (!goal) {
                console.warn(`[TrainSimScene] No goal for ${trainId}`);
                continue;
            }
            const train = new TrainSimTrain({
                trainId,
                spawnCol: spawn.c,
                spawnRow: spawn.r,
                spawnDir: spawn.props?.dir ?? 1,
                startDelay: spawn.props?.startDelay ?? 0,
                goalCol: goal.c,
                goalRow: goal.r,
                cellSize,
                color: TRAINSIM_CONFIG.TRAIN_COLORS[trainId] || 0xffffff,
                themeManager: this.game.theme,
                // Destination chip shown above the head wagon. Looked
                // up via the board's resolver — it knows about each
                // goal's city override + the trainId-to-station map,
                // so a stage that uses a custom destinationCity prop
                // gets the right name without scene-side branching.
                destinationCity: this._board?._goalDisplayCity?.(goal) || '',
            });
            gameplayLayer.addChild(train.gfx);
            this._trains.push(train);
        }
    }

    _createThemeBackground() {
        this._themeBg = createThemeBackground(this.game, 'trainsim.background');
        if (this._themeBg) this.container.addChild(this._themeBg);
    }

    _createHud() {
        // The DOM HUD overlay (TrainSimHud) now owns the schedule,
        // switch / lamp panels and the legend, so the legacy Pixi
        // "Train Simulator X/Y arrived" banner would just duplicate
        // information and steal a chunk of the top-left of the play
        // area. We keep `_messageText` for fatal-error messaging only.
        this._messageText = new Text({ text: '', style: MSG_STYLE });
        this._messageText.anchor.set(0.5);
        this._messageText.x = this.game.width / 2;
        this._messageText.y = this.game.height / 2;
        this._hudContainer.addChild(this._messageText);
    }

    _updateHud() {
        // Live counts and per-train status live in the DOM HUD; nothing
        // to update on the Pixi side beyond fatal messages, which are
        // pushed via `_showFatalMessage` directly.
    }

    _showFatalMessage(text) {
        if (!this._messageText) return;
        this._messageText.text = text;
    }

    // ── input / state ──────────────────────────────────

    /**
     * Proactive danger detection — fires the auto-rewind BEFORE
     * any actual collision happens. Compares each running train's
     * predicted 5-cell path-ahead (the same window the magenta
     * aim-line dots ride on) against every other train's; a single
     * shared cell is enough to trigger.
     *
     * Per client feedback: "the message should appear when the 2
     * magenta lines showing the path touch each other, meaning a
     * 10 cells gap, 5 cells from one train 5 cells from the other
     * train". Catching the danger at the path-overlap stage gives
     * the player ~10s of buffer (5 cells × 2 trains, both rolling
     * at ~1 cell/sec) instead of waiting for the trains to
     * actually meet — by which point the existing last-mile
     * collision guard typically just stalls them in place.
     *
     * @returns {boolean} true if a danger pair was found AND the
     *   rewind was triggered, so the caller can short-circuit the
     *   rest of the frame.
     */
    _detectImpendingPathCollision() {
        if (!this._board || typeof this._board.getTrainAheadCells !== 'function') {
            return false;
        }
        // Post-rewind grace: suppress the detector for a few
        // seconds after the auto-rewind lands so the player can
        // flip switches without immediately re-triggering on the
        // same conflict.
        if (this._dangerCooldownFrames > 0) return false;
        // Gather only trains that are actually rolling — same
        // gating as `getTrainAheadCells` itself so the cell list
        // is non-empty.
        const live = [];
        for (const train of this._trains) {
            if (!train || !train._alive) continue;
            if (!train._started) continue;
            if (train._reachedGoal) continue;
            live.push(train);
        }
        if (live.length < 2) return false;

        // Pre-compute each train's 5-cell ahead path as a Set of
        // "col,row" keys for O(1) lookups during the pairwise
        // intersection scan below.
        const aheadByTrain = new Map();
        for (const train of live) {
            const cells = this._board.getTrainAheadCells(train);
            const set = new Set();
            for (const cell of cells) set.add(`${cell.c},${cell.r}`);
            aheadByTrain.set(train, set);
        }

        for (let i = 0; i < live.length; i++) {
            const a = live[i];
            const aSet = aheadByTrain.get(a);
            if (aSet.size === 0) continue;
            for (let j = i + 1; j < live.length; j++) {
                const b = live[j];
                const bSet = aheadByTrain.get(b);
                if (bSet.size === 0) continue;
                for (const key of aSet) {
                    if (bSet.has(key)) {
                        // First overlap wins — danger rewind takes
                        // effect on this frame either way, so
                        // there's no point in finishing the loop.
                        this._triggerDangerRewind([a, b]);
                        return true;
                    }
                }
            }
        }
        return false;
    }

    _detectCellCollision() {
        const seen = new Map();
        for (const train of this._trains) {
            if (!train._started) continue;
            if (train._reachedGoal) continue;
            if (!train._alive) continue;
            const cell = train.headCell;
            const key = `${cell.c},${cell.r}`;
            const other = seen.get(key);
            if (other) {
                // Two trains landed on the same cell — both go up,
                // and the player loses two lives (one per train).
                // Mark them dead so the main loop doesn't double-
                // count them next frame, then end the game if
                // lives ran out.
                other._alive = false;
                train._alive = false;
                // Per client feedback: same-cell collisions are no
                // longer fatal. Pulse both crashed trains during
                // the brief highlight phase, then roll the sim
                // back ~10s — the rewind also restores `_alive`.
                this._triggerDangerRewind([other, train]);
                return true;
            }
            seen.set(key, train);
        }
        return false;
    }

    /**
     * Credit a train's first goal arrival. Idempotent — re-calling
     * for the same train (e.g. every frame while it sits on the
     * goal cell) doesn't keep adding points.
     */
    _registerGoal(train) {
        if (!train || this._scoredTrains.has(train.trainId)) return;
        this._scoredTrains.add(train.trainId);
        this._score += TRAINSIM_CONFIG.POINTS_PER_TRAIN_GOAL || 250;
    }

    /**
     * Debit one life for a freshly-crashed train. Also idempotent
     * so the per-frame update + the cell-collision detector can
     * both call it for the same crash event without double-
     * penalising the player. `reason` is 'crash' (collision, dead
     * end) or 'wrong-dest' (landed at another train's station) —
     * we use it to pick the right Hungarian warning message.
     */
    _registerCrash(train, reason = 'crash') {
        if (!train || this._lostLifeTrains.has(train.trainId)) return;
        this._lostLifeTrains.add(train.trainId);
        this._lives = Math.max(0, this._lives - 1);
        const idLabel = train.trainId.toUpperCase();
        const tplKey = reason === 'wrong-dest'
            ? 'trainsim.wrong_dest_warning'
            : 'trainsim.crash_warning';
        const fallback = reason === 'wrong-dest'
            ? 'Train {id} arrived at the WRONG destination! Be more careful next time.'
            : 'Train {id} crashed! Be more careful next time.';
        const msg = (t(tplKey) || fallback).replace('{id}', idLabel);
        this._domHud?.showToast?.(msg, 'error', 5500);
    }

    _onAllReachedGoal() {
        this._allReachedGoal = true;
        this._hudContainer.visible = false;
        if (this._domHud) { this._domHud.destroy(); this._domHud = null; }
        partyMode();
        this._playFinishedSong();

        // Use the live score (per-goal credit) — falls back to the
        // legacy "trains × points-per-train" if nothing was scored
        // (shouldn't normally happen, but guards against an empty
        // _scoredTrains set for any reason).
        const totalScore = this._score
            || this._trains.length * (TRAINSIM_CONFIG.POINTS_PER_TRAIN_GOAL || 0);

        this._completeOverlay = new LevelCompleteOverlay();
        this._completeOverlay.show(this.game.width, this.game.height, {
            title: t('common.you_win'),
            lines: [
                `Trains delivered: ${this._trains.length}`,
                `Stage: ${this._stage?.name || ''}`,
            ],
            score: totalScore,
            scoreLabel: t('common.total_score'),
            onContinue: () => this._showScoreOverlay(totalScore),
        });
        this.container.addChild(this._completeOverlay.container);
    }

    /**
     * Play the one-shot victory jingle when every train has reached its
     * goal and the stage is won. The looping ambience layers are paused
     * first so the song plays clean over a quiet bed (the game is over,
     * so they stay down until the scene unmounts). Best-effort: any
     * autoplay rejection is swallowed since the player has already been
     * interacting with the board by the time they win.
     */
    _playFinishedSong() {
        // Duck the background loops so the finished song stands alone.
        try { this._ambienceEl?.pause?.(); } catch (_) {}
        try { this._windAmbienceEl?.pause?.(); } catch (_) {}
        try {
            const el = new Audio('assets/simulator/sounds/game-finished.mp3');
            el.loop = false;
            el.preload = 'auto';
            el.volume = 0.9;
            this._finishedSfxEl = el;
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (_) { /* audio unavailable — non-fatal */ }
    }

    /**
     * Play the one-shot "train has arrived" jingle when a single train
     * reaches its destination. A fresh Audio element per call so trains
     * arriving in quick succession layer their chimes instead of cutting
     * each other off. We keep a handle to the latest so `destroy()` can
     * stop it if the scene unmounts mid-celebration. Best-effort: any
     * autoplay/decoding failure is swallowed.
     */
    _playArrivalSound() {
        try {
            const el = new Audio('assets/simulator/sounds/train-has-arrived.mp3');
            el.loop = false;
            el.preload = 'auto';
            el.volume = 0.85;
            this._arrivalSfxEl = el;
            const p = el.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (_) { /* audio unavailable — non-fatal */ }
    }

    _showScoreOverlay(totalScore) {
        if (this._completeOverlay) { this._completeOverlay.destroy?.(); this._completeOverlay = null; }
        this._scoreOverlay = new ScoreSubmitOverlay();
        this._scoreOverlay.show({
            game: 'trainsim',
            score: totalScore,
            outcome: 'win',
            meta: {
                stage: this._stage?.id,
                trains: this._trains.length,
            },
            theme: this.game.theme,
            onDone: () => {
                this._scoreOverlay = null;
                this._restartGame();
            },
        });
    }

    /**
     * Trigger the danger auto-rewind. Replaces the old crash →
     * lose-life → game-over flow per client feedback ("Ne legyen
     * ütközés…"). The flow is:
     *
     *   1. Pause gameplay (`_dangerRewindActive = true`) — the main
     *      update loop short-circuits to `_tickDangerRewind`.
     *   2. Show the centred "veszélyes közlekedési helyzet" banner
     *      via the HUD.
     *   3. Pulse the crashed train(s) for
     *      `TRAINSIM_DANGER_HIGHLIGHT_FRAMES` frames so the player
     *      can see what just went wrong.
     *   4. Auto-arm the rewind animation with a target ~10s in the
     *      past (or the oldest snapshot we have, if the run is
     *      young).
     *   5. When the rewind animation completes, `_finishRewindAnim`
     *      hides the banner and clears `_dangerRewindActive`.
     *
     * @param {import('./TrainSimTrain.js').TrainSimTrain[]} crashedTrains
     *     Trains to highlight during the brief pulse phase. One
     *     entry for solo derailments, two for same-cell collisions.
     * @param {object} [opts]
     * @param {'crash'|'wrong-dest'} [opts.reason='crash'] What
     *     triggered the rewind. `'wrong-dest'` swaps in the dedicated
     *     wrong-destination banner and a longer 30s rewind window.
     */
    _triggerDangerRewind(crashedTrains = [], { reason = 'crash' } = {}) {
        if (this._dangerRewindActive) return;
        this._dangerRewindActive = true;
        this._dangerHighlightFrames = TRAINSIM_DANGER_HIGHLIGHT_FRAMES;
        this._dangerHighlightTrains = (crashedTrains || [])
            .filter(Boolean)
            .map((train) => ({ train, baseAlpha: train.gfx?.alpha ?? 1 }));

        // Wrong-destination gets a longer rewind window than a
        // collision: the offending train may be far past the last
        // junction with no manual reverses left, so we roll back 30s
        // (vs the usual 10s) to land it before a switch the player can
        // still flip. Consumed by `_armDangerRewindAnim`.
        const isWrongDest = reason === 'wrong-dest';
        this._dangerRewindSeconds = isWrongDest
            ? TrainSimScene._WRONG_DEST_REWIND_SECONDS
            : TrainSimScene._REWIND_SECONDS;

        // Same train-reverse cue as the manual VISSZA button — this
        // is the "about to collide, reversing" case the client asked
        // for. Fired once when the danger flow arms; the actual
        // backwards animation follows after the highlight dwell.
        this._playReverseSound();

        // Surface the message immediately so the player reads it
        // while the trains are pulsing — the banner stays up
        // through the rewind animation and is cleared by
        // `_finishRewindAnim` when the trains are settled back. Wrong
        // destination shows its own dedicated message instead of the
        // generic collision banner.
        const text = isWrongDest
            ? (t('trainsim.wrong_dest_banner')
                || 'Sorry, this is the wrong destination for this train.')
            : (t('trainsim.danger_banner')
                || 'This is a dangerous traffic situation, please start over.');
        this._domHud?.showDangerBanner?.(text);

        // Aim-line / path-preview artefacts are tied to cell
        // coordinates that won't update while we're frozen. Clear
        // them now so the player isn't staring at a "future ghost"
        // pointing at the spot where they just crashed.
        if (this._board && typeof this._board.clearAimLines === 'function') {
            this._board.clearAimLines();
        }
    }

    /**
     * Drive the danger auto-rewind state machine. Two phases:
     *
     *   • Highlight (`_dangerHighlightFrames > 0`) — pulse the
     *     crashed train(s) alpha so they're visibly the cause.
     *     When the timer reaches zero, alphas are restored and we
     *     arm the rewind animation via `_armDangerRewindAnim`.
     *
     *   • Animating (`_rewindAnim` is set with `fromDanger: true`)
     *     — let `_tickRewindAnim` lerp the trains backwards. The
     *     animation lands in `_finishRewindAnim`, which clears the
     *     banner and `_dangerRewindActive` so play resumes.
     */
    _tickDangerRewind(delta) {
        if (this._dangerHighlightFrames > 0) {
            this._dangerHighlightFrames -= delta;
            const phase = performance.now() * TRAINSIM_HIGHLIGHT_PULSE_SPEED;
            const t01 = (Math.sin(phase) + 1) * 0.5;
            const alpha = TRAINSIM_HIGHLIGHT_ALPHA_MIN
                + (TRAINSIM_HIGHLIGHT_ALPHA_MAX - TRAINSIM_HIGHLIGHT_ALPHA_MIN) * t01;
            for (const entry of this._dangerHighlightTrains) {
                if (entry.train?.gfx) entry.train.gfx.alpha = alpha;
            }

            if (this._dangerHighlightFrames > 0) return;

            // Highlight phase done — restore alpha and arm the
            // rewind animation. The rewind itself takes
            // `_REWIND_ANIM_SECONDS` (~0.55s) and lands in
            // `_finishRewindAnim`.
            for (const entry of this._dangerHighlightTrains) {
                if (entry.train?.gfx) entry.train.gfx.alpha = entry.baseAlpha;
            }
            this._dangerHighlightTrains = [];
            this._armDangerRewindAnim();
            return;
        }

        // Animating phase — drive the existing rewind tick.
        if (this._rewindAnim) {
            this._tickRewindAnim(delta);
            return;
        }

        // No animation armed (e.g. snapshot history was empty and
        // we couldn't rewind). Bail back to a clean state — clear
        // banner + flags and just resume from where we are. The
        // crashed-train state was reset by the per-train
        // collision logic, so the sim will tick normally.
        this._finishDangerRewind();
    }

    /**
     * Pick the rewind target snapshot and arm `_rewindAnim` for
     * the auto-rewind path. Differences from the manual
     * `_rewindGame()`:
     *
     *   • Doesn't require ≥10s of history — falls back to the
     *     oldest available snapshot if the player crashed early.
     *   • Doesn't decrement `_rewindsRemaining`. The 5-uses pool
     *     is for the manual REWIND button only.
     *   • Doesn't show a toast — we already have the banner up.
     *   • Tags the animation with `fromDanger: true` so
     *     `_finishRewindAnim` knows to dismiss the banner +
     *     clear `_dangerRewindActive` when it lands.
     */
    _armDangerRewindAnim() {
        if (this._rewindAnim) return; // already running
        if (this._snapshotHistory.length === 0) {
            // Brand-new game with no snapshots yet — best we can
            // do is restart the stage from scratch. The banner
            // would be re-shown by the new scene if the same
            // crash happens again.
            this._finishDangerRewind();
            this._restartGame();
            return;
        }

        // Prefer a snapshot `_dangerRewindSeconds` in the past (10s for
        // a collision, 30s for a wrong destination), but fall back to
        // the oldest available if the run is younger than that —
        // auto-rewind should never refuse, only land closer to the start.
        const rewindSeconds = this._dangerRewindSeconds
            || TrainSimScene._REWIND_SECONDS;
        const target = this._elapsedSeconds - rewindSeconds;
        let chosenIdx = -1;
        let chosenDelta = Infinity;
        for (let i = this._snapshotHistory.length - 1; i >= 0; i--) {
            const snap = this._snapshotHistory[i];
            if (snap.elapsedSeconds > target) continue;
            const d = Math.abs(snap.elapsedSeconds - target);
            if (d < chosenDelta) {
                chosenDelta = d;
                chosenIdx = i;
            }
        }
        if (chosenIdx < 0) chosenIdx = 0;

        // Build the keyframe list (current frame + history slice
        // back to the chosen target). Mirrors the manual rewind
        // setup so the same `_tickRewindAnim` driver can lerp
        // through it.
        const nowSnap = this._buildCurrentSnapshot();
        const keySnaps = [nowSnap];
        for (let i = this._snapshotHistory.length - 1; i >= chosenIdx; i--) {
            keySnaps.push(this._snapshotHistory[i]);
        }
        const frames = keySnaps.map((snap) => {
            const trains = {};
            for (const train of this._trains) {
                const tsnap = snap.trains && snap.trains[train.trainId];
                if (!tsnap) continue;
                train.restore(tsnap);
                if (typeof train.render === 'function') train.render();
                trains[train.trainId] = {
                    head: { x: train._headGfx.x, y: train._headGfx.y, rot: train._headGfx.rotation },
                    tail: { x: train._tailGfx.x, y: train._tailGfx.y, rot: train._tailGfx.rotation },
                    visible: !!train.gfx.visible,
                };
            }
            return { trains };
        });
        // Restore CURRENT-frame state so the first animation tick
        // continues from the same pixels the player was looking at.
        for (const train of this._trains) {
            const tsnap = nowSnap.trains && nowSnap.trains[train.trainId];
            if (tsnap) {
                train.restore(tsnap);
                if (typeof train.render === 'function') train.render();
            }
        }

        this._rewindAnim = {
            frames,
            duration: TrainSimScene._REWIND_ANIM_SECONDS,
            elapsed: 0,
            targetSnap: this._snapshotHistory[chosenIdx],
            targetIdx: chosenIdx,
            // Marker consumed by `_finishRewindAnim` so it knows
            // this rewind originated from a crash and needs to
            // clear the banner + the `_dangerRewindActive` flag.
            fromDanger: true,
        };
    }

    /**
     * Tear down the danger-rewind UI state. Called by
     * `_finishRewindAnim` (when a `fromDanger` rewind lands) or
     * by `_tickDangerRewind` if the rewind couldn't be armed.
     */
    _finishDangerRewind() {
        this._dangerRewindActive = false;
        this._dangerHighlightFrames = 0;
        // Make sure no train is left half-faded from a highlight
        // phase that got interrupted.
        for (const entry of this._dangerHighlightTrains) {
            if (entry.train?.gfx) entry.train.gfx.alpha = entry.baseAlpha;
        }
        this._dangerHighlightTrains = [];
        this._domHud?.hideDangerBanner?.();
        // Arm the post-rewind cooldown so the proactive detector
        // doesn't immediately re-fire on the same still-conflicting
        // state. The countdown ticks down inside `update()`.
        this._dangerCooldownFrames = TRAINSIM_DANGER_COOLDOWN_FRAMES;
    }

    /**
     * Build + show the DOM Game Over modal. Currently unused
     * because all crash paths now route through the danger
     * auto-rewind flow per client feedback, but kept around for
     * any future "soft" game-over trigger (e.g. a future
     * time-limit mode).
     */
    _showGameOverOverlay(reason) {
        if (this._domHud) { this._domHud.destroy(); this._domHud = null; }
        this._gameOverOverlay?.destroy?.();
        this._gameOverOverlay = new GameOverOverlay();
        this._gameOverOverlay.show(this.game.width, this.game.height, {
            title: t('common.game_over'),
            won: false,
            subtitle: reason,
            actions: [
                {
                    kind: 'home',
                    label: t('common.main_menu') || 'MAIN MENU',
                    onClick: () => this._handleBack(),
                },
                {
                    kind: 'retry',
                    label: t('common.retry') || 'AGAIN',
                    primary: true,
                    onClick: () => this._restartGame(),
                },
            ],
        });
        this.container.addChild(this._gameOverOverlay.container);
    }

    _restartGame() {
        this.destroy();
        this.container.removeChildren();
        this.init(this.game);
    }

    /**
     * Capture a full snapshot of the live game state. Shared between
     * the periodic history-push (`_maybeTakeSnapshot`) and the
     * REWIND animation, which needs a "current frame" to animate
     * AWAY from.
     */
    _buildCurrentSnapshot() {
        return {
            elapsedSeconds: this._elapsedSeconds,
            score: this._score,
            lives: this._lives,
            scoredTrains: Array.from(this._scoredTrains),
            lostLifeTrains: Array.from(this._lostLifeTrains),
            // Map train_id → train_snapshot so the restore step can
            // pair up trains by id (robust against re-ordering even
            // though the array is currently stable).
            trains: this._trains.reduce((acc, t) => {
                acc[t.trainId] = t.snapshot();
                return acc;
            }, {}),
            entities: this._board.snapshotEntityStates(),
        };
    }

    /**
     * Push a fresh snapshot of train + switch + lamp + scoring state
     * onto the rolling history buffer, but only once per
     * `_SNAPSHOT_INTERVAL` game-seconds. The buffer length is hard-
     * capped at `_SNAPSHOT_HISTORY_MAX` — older entries fall off the
     * front so memory stays flat regardless of stage length.
     *
     * Called from `update()` after the in-world clock advances.
     * Cheap: snapshots are plain primitive objects, no DOM / GPU work.
     */
    _maybeTakeSnapshot() {
        if (!this._board) return;
        if (this._trains.length === 0) return;
        // Don't snapshot the game-over / level-complete frames —
        // they're terminal and rewinding into them is meaningless.
        if (this._gameOver || this._allReachedGoal) return;

        const interval = TrainSimScene._SNAPSHOT_INTERVAL;
        if (this._lastSnapshotAt >= 0
            && (this._elapsedSeconds - this._lastSnapshotAt) < interval) {
            return;
        }
        this._lastSnapshotAt = this._elapsedSeconds;

        this._snapshotHistory.push(this._buildCurrentSnapshot());
        // Evict oldest history beyond the cap. With INTERVAL=1s and
        // MAX=6, that's a sliding window of the last 6 seconds.
        const max = TrainSimScene._SNAPSHOT_HISTORY_MAX;
        while (this._snapshotHistory.length > max) {
            this._snapshotHistory.shift();
        }
    }

    /**
     * Kick off a "VCR fast rewind" animation that visually walks
     * every train back through the snapshot timeline towards a
     * snapshot ~`_REWIND_SECONDS` of game-time in the past. The
     * actual restore-of-truth (switches, lamps, score, lives) lands
     * at the END of the animation in `_finishRewindAnim` so the
     * settled state is consistent with the resting position the
     * player sees.
     *
     * Bounded by `_rewindsRemaining` (the HUD pill mirrors this).
     * Returns `true` when a rewind was successfully QUEUED so the
     * HUD can sync its disabled state + count immediately.
     */
    _rewindGame() {
        if (this._rewindsRemaining <= 0) return false;
        if (this._gameOver || this._allReachedGoal) return false;
        if (this._snapshotHistory.length === 0) return false;
        // Don't stack rewinds — one click, one animation.
        if (this._rewindAnim) return false;
        // Manual rewind is locked while the danger auto-rewind is
        // running — otherwise the player could spam REWIND during
        // the highlight phase and consume one of their 5 manual
        // rewinds for free.
        if (this._dangerRewindActive) return false;

        // 1) Find target snapshot. We always land at or BEFORE
        //    (now − REWIND_SECONDS) so the press buys the player
        //    a FULL window of game time — never a partial half-
        //    rewind that silently shortchanges them. If no snapshot
        //    older than the target exists (e.g. the stage has been
        //    running for less than REWIND_SECONDS), we DENY the
        //    press without burning a use — far better UX than
        //    consuming one of five precious rewinds for a 2-second
        //    jump the player didn't ask for.
        const target = this._elapsedSeconds - TrainSimScene._REWIND_SECONDS;
        let chosenIdx = -1;
        let chosenDelta = Infinity;
        for (let i = this._snapshotHistory.length - 1; i >= 0; i--) {
            const snap = this._snapshotHistory[i];
            if (snap.elapsedSeconds > target) continue;
            const delta = Math.abs(snap.elapsedSeconds - target);
            if (delta < chosenDelta) {
                chosenDelta = delta;
                chosenIdx   = i;
            }
        }
        if (chosenIdx < 0) {
            // History doesn't reach back the full REWIND_SECONDS
            // window — refuse the press, surface a friendly toast
            // so the player understands why nothing happened, and
            // return without decrementing `_rewindsRemaining`.
            const tooEarlyTpl = t('trainsim.rewind_too_early')
                || 'Wait a bit — need {sec}s of play before the first rewind.';
            const msg = tooEarlyTpl.replace('{sec}', String(Math.round(TrainSimScene._REWIND_SECONDS)));
            this._domHud?.showToast?.(msg, 'warn', 2400);
            return false;
        }

        // Rewind is going ahead — play the train-reverse cue now so
        // the audio lines up with the backwards roll the player is
        // about to see. (Placed AFTER the deny-paths above so a
        // refused press stays silent.)
        this._playReverseSound();

        // 2) Snapshot the CURRENT state so the animation has a
        //    "now" frame to start from — without this the first
        //    keyframe would be the most-recent HISTORY snapshot,
        //    which can be up to one full _SNAPSHOT_INTERVAL behind
        //    the live trains and would look like a small forward
        //    skip before the rewind starts.
        const nowSnap = this._buildCurrentSnapshot();

        // 3) Build the animation keyframe list, newest → oldest.
        //    Pixel coordinates are captured per train per frame by
        //    briefly restoring the snapshot and reading the
        //    rendered sprite position. This bakes the curved-track
        //    interpolation into each waypoint so the rewind tween
        //    follows the rails instead of cutting straight lines.
        const keySnaps = [nowSnap];
        for (let i = this._snapshotHistory.length - 1; i >= chosenIdx; i--) {
            keySnaps.push(this._snapshotHistory[i]);
        }
        const frames = keySnaps.map((snap) => {
            const trains = {};
            for (const train of this._trains) {
                const tsnap = snap.trains && snap.trains[train.trainId];
                if (!tsnap) continue;
                train.restore(tsnap);
                if (typeof train.render === 'function') train.render();
                trains[train.trainId] = {
                    head: {
                        x: train._headGfx.x,
                        y: train._headGfx.y,
                        rot: train._headGfx.rotation,
                    },
                    tail: {
                        x: train._tailGfx.x,
                        y: train._tailGfx.y,
                        rot: train._tailGfx.rotation,
                    },
                    visible: !!train.gfx.visible,
                };
            }
            return { trains };
        });

        // 4) Restore trains to the CURRENT state so the very first
        //    animation tick continues from the same pixel coords
        //    the player was looking at — the keyframe sweep
        //    mutated train state in step 3.
        for (const train of this._trains) {
            const tsnap = nowSnap.trains && nowSnap.trains[train.trainId];
            if (tsnap) {
                train.restore(tsnap);
                if (typeof train.render === 'function') train.render();
            }
        }

        // 5) Clear any aim-line / path-preview artefacts left from
        //    pre-rewind play — they're anchored to cell coords that
        //    won't change until the animation lands, so leaving
        //    them on screen creates a jarring "future ghost" while
        //    the trains slide backwards.
        if (this._board && typeof this._board.clearAimLines === 'function') {
            this._board.clearAimLines();
        }

        // 6) Arm the animation. `_tickRewindAnim` drives it from
        //    here, and `_finishRewindAnim` cleans up + applies the
        //    final target snapshot at the end.
        this._rewindAnim = {
            frames,
            duration: TrainSimScene._REWIND_ANIM_SECONDS,
            elapsed: 0,
            targetSnap: this._snapshotHistory[chosenIdx],
            targetIdx: chosenIdx,
        };
        this._rewindsRemaining--;

        // Toast + HUD sync now so the player sees feedback the
        // instant they click, not after the animation finishes.
        const msg = (t('trainsim.rewind_toast') || 'Rewinding {sec}s — {left} REWIND left')
            .replace('{sec}',  String(Math.round(TrainSimScene._REWIND_SECONDS)))
            .replace('{left}', String(this._rewindsRemaining));
        this._domHud?.showToast?.(msg, 'info', 2200);
        this._domHud?.syncRewindButton?.();
        return true;
    }

    /**
     * Per-frame driver for the REWIND animation. Lerps each train's
     * head + tail sprite between the two surrounding keyframes
     * using an ease-in-out so the rewind feels weighty rather than
     * mechanical. When the animation completes, `_finishRewindAnim`
     * applies the target snapshot for full state-of-truth restore.
     */
    _tickRewindAnim(delta) {
        const anim = this._rewindAnim;
        if (!anim) return;
        anim.elapsed += (delta / 60);
        const tRaw = anim.duration > 0
            ? Math.min(1, anim.elapsed / anim.duration)
            : 1;

        // Smoothstep — gentle wind-up + slow-down so the rewind
        // doesn't snap on either end of the timeline.
        const t = tRaw * tRaw * (3 - 2 * tRaw);

        const frames = anim.frames;
        const last   = frames.length - 1;
        const idxF   = t * last;
        const i      = Math.min(last - 1, Math.floor(idxF));
        const u      = Math.min(1, Math.max(0, idxF - i));

        const a = frames[i];
        const b = frames[i + 1] || a;

        // Shortest-arc rotation lerp so the train head doesn't
        // spin the long way around when crossing the ±π boundary.
        const lerpAngle = (a0, a1, w) => {
            const TAU = Math.PI * 2;
            let diff = ((a1 - a0) % TAU + TAU) % TAU;
            if (diff > Math.PI) diff -= TAU;
            return a0 + diff * w;
        };
        const lerp = (a0, a1, w) => a0 + (a1 - a0) * w;

        for (const train of this._trains) {
            const aPos = a.trains[train.trainId];
            const bPos = b.trains[train.trainId];
            if (!aPos || !bPos) continue;

            // Container visibility: a train that hasn't STARTED in
            // the older frame should fade out as the rewind crosses
            // its spawn moment. Cheap proxy — alpha-fade the gfx
            // container over the local lerp window.
            train.gfx.visible = aPos.visible || bPos.visible;
            train.gfx.alpha = aPos.visible && bPos.visible
                ? 1
                : (aPos.visible ? (1 - u) : (bPos.visible ? u : 0));

            train._headGfx.x = lerp(aPos.head.x, bPos.head.x, u);
            train._headGfx.y = lerp(aPos.head.y, bPos.head.y, u);
            train._headGfx.rotation = lerpAngle(aPos.head.rot, bPos.head.rot, u);

            train._tailGfx.x = lerp(aPos.tail.x, bPos.tail.x, u);
            train._tailGfx.y = lerp(aPos.tail.y, bPos.tail.y, u);
            train._tailGfx.rotation = lerpAngle(aPos.tail.rot, bPos.tail.rot, u);
        }

        if (tRaw >= 1) this._finishRewindAnim();
    }

    /**
     * Land the rewind animation on its target snapshot — apply
     * the source-of-truth state (switches, lamps, score, lives,
     * elapsed clock), call render() on each train to re-sync the
     * sprite to its restored cell, and clear `_rewindAnim` so
     * normal `update()` resumes on the next tick.
     */
    _finishRewindAnim() {
        const anim = this._rewindAnim;
        if (!anim) return;
        const snap = anim.targetSnap;
        const fromDanger = !!anim.fromDanger;

        this._elapsedSeconds = snap.elapsedSeconds;
        this._lastSnapshotAt = snap.elapsedSeconds;
        this._score = snap.score;
        this._lives = snap.lives;
        this._scoredTrains   = new Set(snap.scoredTrains   || []);
        this._lostLifeTrains = new Set(snap.lostLifeTrains || []);

        for (const train of this._trains) {
            const tsnap = snap.trains && snap.trains[train.trainId];
            if (tsnap) {
                train.restore(tsnap);
                // Container alpha is reset here so a rewind that
                // brought a train back across its spawn boundary
                // doesn't leave it half-transparent forever.
                train.gfx.alpha = 1;
                if (typeof train.render === 'function') train.render();
            }
        }
        if (this._board && typeof this._board.restoreEntityStates === 'function') {
            this._board.restoreEntityStates(snap.entities);
        }

        // Burn off everything from the chosen snapshot forward so
        // the NEXT rewind walks further into the past instead of
        // re-using the same restore point.
        this._snapshotHistory = this._snapshotHistory.slice(0, anim.targetIdx);
        this._rewindAnim = null;
        this._domHud?.syncRewindButton?.();

        // If this was an auto-rewind from a danger event, clear
        // the banner + the `_dangerRewindActive` gate so the main
        // update loop resumes normally on the next tick.
        if (fromDanger) {
            this._finishDangerRewind();
        }
    }

    /**
     * Recompute the visible `_paused` flag from the two contributing
     * inputs (`_userPaused`, `_chromePaused`). Called every time
     * either flag changes — keeping the single consumer-side flag
     * (`_paused`, read by `update()`) consistent without forcing the
     * update loop to do the OR itself.
     */
    _recomputePause() {
        this._paused = !!(this._userPaused || this._chromePaused);
    }

    /**
     * Flip the player's deliberate pause. Called from the HUD's
     * PAUSE tile. While `_userPaused` is true, `update()` short-
     * circuits and every part of the sim freezes (trains, road
     * traffic, switch / lamp animations, the in-world clock,
     * snapshot history pushes). Resume returns to whatever speed
     * multiplier and focus mode were active before.
     *
     * Returns the new paused state so the HUD can refresh its tile.
     */
    _togglePause() {
        this._userPaused = !this._userPaused;
        this._recomputePause();
        return this._userPaused;
    }

    /**
     * Flip FOCUS mode. While ON, the board's focus overlay dims
     * everything below the gameplay layer (background sprite, grid,
     * background layer, road layer) so the tracks, route bands,
     * switches, lamps and trains pop out of a darker scene. State
     * is persisted via `window.__trainsimFocus` so a stage restart
     * keeps the player's chosen visibility mode.
     *
     * Returns the new focus state so the HUD can refresh its tile.
     */
    _toggleFocus() {
        this._focusMode = !this._focusMode;
        if (typeof window !== 'undefined') window.__trainsimFocus = this._focusMode;
        if (this._board && typeof this._board.setFocusMode === 'function') {
            this._board.setFocusMode(this._focusMode);
        }
        return this._focusMode;
    }

    /**
     * Advance the train-speed multiplier to the next preset in the
     * static `_SPEED_STEPS` array, wrapping back to the first step
     * after the last. Persists across rebuilds via window.__trainsimSpeed.
     * Returns the new multiplier so the HUD can refresh its label.
     */
    _cycleSpeed() {
        const steps = TrainSimScene._SPEED_STEPS;
        const cur = this._speedMultiplier || 1;
        const idx = steps.indexOf(cur);
        const next = steps[(idx + 1) % steps.length];
        this._speedMultiplier = next;
        if (typeof window !== 'undefined') window.__trainsimSpeed = next;
        return next;
    }

    _handleBack() {
        // The Stage Editor sets `game.fromEditor` before launching a
        // test play, so the back button takes the dev straight back to
        // the editor with their in-progress stage intact. Production
        // builds strip the Stage Editor entirely, so this branch is
        // gated on the same `globalThis.__PRODUCTION_BUILD__` flag and
        // gets dead-code eliminated alongside the editor scene itself.
        if (!globalThis.__PRODUCTION_BUILD__ && this.game.fromEditor) {
            this.game.fromEditor = false;
            // Resume the editing session we test-played from (restore the
            // working stage) instead of reopening the game-selector launcher.
            this.game.editorTargetGame = 'trainsim';
            this.game.editorResume = true;
            this.game.sceneManager.switchScene('stage-editor');
            return;
        }
        this.game.sceneManager.switchScene('menu');
    }

    _buildHelp() {
        return {
            title: t('trainsim.help_title'),
            sections: [
                { heading: t('help.goal'),      color: 0xfbbf24,
                  body: t('trainsim.help_goal') },
                { heading: t('help.controls'),  color: 0xfbbf24,
                  body: t('trainsim.help_controls') },
                { heading: t('help.junctions'), color: 0xa855f7,
                  body: t('trainsim.help_junctions') },
                { heading: t('help.trains'),    color: 0xa855f7,
                  body: t('trainsim.help_trains') },
            ],
        };
    }

    /**
     * One-shot migration for legacy stage JSON (`schemaVersion` < 2):
     * doubles every spawn entity's `dir` so the old 4-way cardinal
     * encoding (0=N, 1=E, 2=S, 3=W) lines up with the current 8-way
     * encoding (0=N, 1=NE, 2=E, 3=SE, ...). Mutates `stage` in place
     * and stamps it as v2 so this never runs twice on the same object.
     */
    static _migrateLegacyDirs(stage) {
        if (!stage || typeof stage !== 'object') return;
        if ((stage.schemaVersion || 1) >= 2) return;
        for (const layer of (stage.layers || [])) {
            for (const ent of (layer.entities || [])) {
                if (ent.type !== 'spawn') continue;
                const d = ent.props?.dir;
                if (typeof d === 'number' && d >= 0 && d <= 3) {
                    ent.props.dir = d * 2;
                }
            }
        }
        stage.schemaVersion = 2;
    }
}
